import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { read, utils, writeFile } from 'xlsx';

// Icons
import { ArrowDownTrayIcon, ClipboardDocumentListIcon, PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";

// Context
import LoaderContext from "../../../../context/psikotest/LoaderContext";
import SnackbarContext from "../../../../context/psikotest/SnackbarContext";

// Helper
import errorHandler from "../../../../helper/psikotest/errorHandler";

// Controller
import AssessmentParticipant from "../../../../controller/psikotest/psikotest_system/assessment_participant";

function FormImportParticipant(
    props: {
        assessment_id: number,
        handleClose: () => void,
        handleRefreshAfterImport: () => void
    }) {
    // Contexts
    const { setMessage } = useContext(LoaderContext);
    const { setNotif } = useContext(SnackbarContext);

    // Ref
    const uploadInput = useRef<HTMLInputElement>(null);

    // State
    const [data, setData] = useState<TPSParticipantImport[]>([])
    const [file, setFile] = useState<File>();
    const [onSubmit, setOnSubmit] = useState<boolean>(false)

    // handle read file import
    const readFileImport = useCallback(async (file: File) => {
        try {
            // check file type, make sure it xlsx
            if (!file.type.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
                throw new Error("file type not valid, only accept .xlsx!");
            }
            const fileBuffer = await file.arrayBuffer();
            const wb = read(fileBuffer); // parse the array buffer
            const ws = wb.Sheets[wb.SheetNames[0]]; // get the first worksheet
            const result: any = utils.sheet_to_json(ws); // generate json
            // return alert if empty
            if (result.length === 0) {
                throw new Error("not found any data!");
            }
            // column 
            const columnHeader = ["full_name", "email"]
            // check key 
            const valid = Object.keys(result?.[0] ?? {}).filter(k => columnHeader.includes(k)).length >= columnHeader.length
            // return error if not valid
            if (!valid) {
                throw new Error("format data not valid, please check if any column missing!");
            }
            // set status
            const dataUpdateStatus: TPSParticipantImport[] = result.map((r: TPSParticipantImport): TPSParticipantImport => {
                return { ...r, email: r.email.replace(/\s+/g, ''), status: "pending", message: "" }
            })
            // set data for status
            setData(dataUpdateStatus)
        } catch (error) {
            const errorMessage = errorHandler(error);
            setNotif({ type: "error", message: errorMessage });
        }
    }, [setNotif])
    // download file template
    const downloadFileTemplate = () => {
        let data = []
        // header
        data.push({})
        // generate worksheet and workbook 
        const worksheet = utils.json_to_sheet(data)
        const workbook = utils.book_new()
        utils.book_append_sheet(workbook, worksheet, "Participant")
        // main header
        let header: string[] = ["full_name", "email"];
        utils.sheet_add_aoa(worksheet, [header], { origin: "A1" });
        // set width
        var wscols = [
            { width: 20, },
            { width: 20 }
        ];
        worksheet["!cols"] = wscols;
        // create an XLSX file and try to save to .xlsx
        writeFile(workbook, `Psikotest Participant Template.xlsx`)
    }
    // import data
    const importData = async () => {
        setMessage("Save Participant");
        // set on submit
        setOnSubmit(true)
        const cacs = new AssessmentParticipant();
        // loop
        for (let i = 0; i < data.length; i++) {
            const dataToProccess = data[i]
            if (dataToProccess.status !== "success") {
                try {
                    await cacs.create({
                        id: 0,
                        assessment_id: props.assessment_id,
                        full_name: dataToProccess.full_name,
                        email: dataToProccess.email.replace(/\s+/g, ''),
                    });
                    setData(updateData => (updateData.map((d, d_i) => {
                        return d_i === i ? { ...d, status: "success", message: "submitted" } : d
                    })))
                } catch (error) {
                    const errorMessage = errorHandler(error);
                    setNotif({ type: "error", message: errorMessage });
                    setData(updateData => (updateData.map((d, d_i) => {
                        return d_i === i ? { ...d, status: "failed", message: errorMessage } : d
                    })))
                }
            }
        }
        setMessage("");
        // set on submit false
        setOnSubmit(false)
    }

    // use effect
    useEffect(() => {
        if (file) {
            readFileImport(file)
        }
    }, [file, readFileImport]);
    // if all data submitted
    useEffect(() => {
        if (data.length > 0 && data.filter(d => d.status === "success").length === data.length) {
            props.handleRefreshAfterImport()
        }
    }, [data, props]);

    return (
        <div>
            <div className="fixed z-20 top-0 left-0 w-screen h-screen bg-black opacity-20"></div>
            <div className="fixed z-30 top-0 left-0 w-screen h-screen bg-transparent flex items-center justify-center">
                <div className="bg-white w-screen md:w-[90%] h-[90%] md:rounded overflow-hidden">
                    <div className="p-4 bg-green-800 md:rounded-t text-white flex justify-between">
                        <div className="flex space-x-2">
                            <ClipboardDocumentListIcon className="w-4" />
                            <h3>Form Import Participant</h3>
                        </div>
                        <XMarkIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent cursor-pointer" aria-hidden="true" onClick={() => props.handleClose()} />
                    </div>
                    <div className="h-[94%] overflow-auto">
                        {data.length === 0
                            ?
                            <div className="h-full flex flex-col justify-center items-center">
                                <div className="pt-2">
                                    <button type="button" disabled={onSubmit}
                                        onClick={() => downloadFileTemplate()}
                                        className="flex items-center border border-blue-600 py-1 px-2 text-blue-600 rounded disabled:bg-slate-600">
                                        <ArrowDownTrayIcon className="w-4" />
                                        <span className="block pl-1">Download File Template</span>
                                    </button>
                                </div>
                                <div className="h-full flex-1 flex flex-col justify-center items-center">
                                    <div className="h-60 w-60 flex flex-col justify-center items-center text-red-400 font-bold border border-red-200 bg-red-50 rounded-full">
                                        <ClipboardDocumentListIcon className="w-10" />
                                        <div>No File Selected</div>
                                        <div className="text-sm font-normal">Click or drag file here</div>
                                    </div>
                                    <input
                                        type="file"
                                        className="absolute h-2/6 w-3/4 lg:w-1/4 opacity-0"
                                        accept=".xlsx"
                                        ref={uploadInput}
                                        onChange={(event) => {
                                            if (event.target.files !== null) {
                                                Array.from(event.target.files).forEach((file, i) => {
                                                    if (i === 0) {
                                                        setFile(file);
                                                    }
                                                });
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            : <div className="p-4">
                                <div className="flex justify-between pb-2">
                                    <div>
                                        <button type="button" disabled={onSubmit}
                                            onClick={() => importData()}
                                            className="flex items-center bg-blue-600 py-1 px-2 text-white rounded disabled:bg-slate-600">
                                            <PaperAirplaneIcon className="w-4 -rotate-45" />
                                            <span className="block pl-1">Submit</span>
                                        </button>
                                    </div>
                                    <div className={`text-lg font-semibold ${data.filter(d => d.status === "success").length === data.length ? "text-green-600" : "text-blue-600"}`}>
                                        {data.filter(d => d.status === "success").length}/{data.length}
                                    </div>
                                </div>
                                <div>
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-700 text-white">
                                                <th className="border font-medium p-2 text-left w-2">#</th>
                                                <th className="border font-medium p-2 text-left min-w-[18rem]">Full Name</th>
                                                <th className="border font-medium p-2 text-left min-w-[18rem]">Email</th>
                                                <th className="border font-medium p-2 text-left min-w-[18rem]">Status</th>
                                                <th className="border font-medium p-2 text-left min-w-[18rem]">Message</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white text-sm">
                                            {data.map((d, i) => (
                                                <tr key={`data_${i}`} className={`${d.status === "success" ? "bg-green-100" : d.status === "failed" ? "bg-red-100" : "bg-slate-100"}`}>
                                                    <td className="border border-gray-300 text-center">{i + 1}</td>
                                                    <td className="border border-gray-300 text-slate-500">
                                                        <input
                                                            type="text"
                                                            className={"border-gray-200 focus:border-gray-500 w-60 px-2 py-1 first-letter:appearance-none bg-white-200 text-gray-700 border focus:outline-none"}
                                                            value={d.full_name}
                                                            disabled={d.status === "success"}
                                                            onChange={(e) => {
                                                                // update data
                                                                const copyData: TPSParticipantImport[] = data.map((d, data_i) => {
                                                                    return data_i === i ? { ...d, full_name: e.target.value } : d
                                                                })
                                                                setData(copyData)
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="border border-gray-300 text-slate-500">
                                                        <input
                                                            type="text"
                                                            className={"border-gray-200 focus:border-gray-500 w-60 px-2 py-1 first-letter:appearance-none bg-white-200 text-gray-700 border focus:outline-none"}
                                                            value={d.email}
                                                            disabled={d.status === "success"}
                                                            onChange={(e) => {
                                                                // update data
                                                                const copyData: TPSParticipantImport[] = data.map((d, data_i) => {
                                                                    return data_i === i ? { ...d, email: e.target.value } : d
                                                                })
                                                                setData(copyData)
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="border border-gray-300 text-slate-500 px-2">
                                                        {d.status ?? "-"}
                                                    </td>
                                                    <td className="border border-gray-300 text-slate-500 px-2">
                                                        {d.message ?? "-"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FormImportParticipant;